import { useState, useEffect, useCallback } from 'react'
import {
  OvbResolveType,
  ReservationMessageTemplateCode,
  useOriginsQuery,
  useRatesQuery,
  useReservationCreateMutation,
  useReservationStatesQuery,
  useReservedResourceCreateMutation,
  useSendReservationEmailMutation,
  RateType,
  useReservationsCreationQuery,
} from 'src/api/api.generated'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import syncMutation from 'src/utils/sync-mutation'
import { ReservationTimeNewFormData } from './reservation-time-new-form.types'

export const useReservationTimeNewCRUD = (tenantId: string, propertyId: string, reservationId?: string) => {
  const showError = useErrorSnackBar()

  const [loading, setLoading] = useState<boolean>(false)

  const [createReservation] = useReservationCreateMutation({
    refetchQueries: ['ReservationSearch'],
  })
  const [addReservedResourceToReservation] = useReservedResourceCreateMutation()
  const [sendReservationEmail] = useSendReservationEmailMutation({
    onError: (error) => {
      showError(error)
      setLoading(false)
    },
  })

  const { data: originsResult, loading: loadingOrigins } = useOriginsQuery({
    variables: {
      tenantId: tenantId,
      pms: true,
    },
  })

  const upsertReservationTime = useCallback(
    async (data: ReservationTimeNewFormData, reservationId?: string, mainGuestName?: string, customerCompanyName?: string, next?: () => void) => {
      setLoading(true)
      if (reservationId) {
        const result = await syncMutation(addReservedResourceToReservation, {
          variables: {
            value: {
              tenantId,
              ...data.reservedResources[0],
              reservationId,
            },
            options: { overbooking: OvbResolveType.ERROR },
          },
        })
        if (result.error) {
          showError(result.error)
        }

        setLoading(false)
        if (next) next()
      } else {
        const result = await syncMutation(createReservation, {
          variables: {
            value: {
              ...data,
              searchInfo: mainGuestName
                ? {
                    mainGuestName,
                    customerCompanyName,
                    customerPersonName: mainGuestName,
                  }
                : undefined,
            },
            options: {
              overbooking: OvbResolveType.ERROR,
            },
          },
        })

        const createdReservation = result.result?.data?.createReservation

        if (createdReservation && !result.error) {
          setLoading(false)
          if (next) next()
          return result.result

          // non si inviano email per prenotazione orarie
          // // Invio contestuale email
          // if (emailAddress) {
          //   let template: ReservationMessageTemplateCode | undefined = undefined
          //   const rr = createdReservation.reservedResources && createdReservation.reservedResources.length > 0 ? createdReservation.reservedResources[0] : undefined

          //   // 1 recupera la policy più restrittiva
          //   // 2 set email template to use.
          //   if (rr && createdReservation.policy) {
          //     switch (createdReservation.policy.paymentRule.type) {
          //       case 'NOTREQUIRED':
          //         if (createdReservation.policy.paymentRule.guaranteedCreditCard) template = 'LINK2PAY'
          //         else template = 'RESERVATION'
          //         break
          //       case 'FIRSTNIGHT':
          //       case 'PERCENTAGE_TOTAL':
          //       case 'TOTAL':
          //         template = 'LINK2PAY'
          //         break
          //     }
          //   }

          //   // 3 Send email
          //   if (template) {
          //     await sendReservationEmail({
          //       variables: {
          //         input: {
          //           tenantId: data.tenantId,
          //           propertyId: data.propertyId[0],
          //           reservationId: createdReservation.id,
          //           sendTo: emailAddress,
          //           messageTemplateId: template,
          //         },
          //       },
          //     })
          //   }
          // }
        } else {
          setLoading(false)
          showError(result.error)
        }
      }
    },
    [createReservation, showError, tenantId],
  )

  const { data: reservationStatesResult, loading: loadingReservationStates } = useReservationStatesQuery({ variables: { tenantId: tenantId } })

  const { data: baseRateQuery, loading: loadingBaseRateQuery } = useRatesQuery({ variables: { filter: { tenantId: { eq: tenantId }, type: RateType.BASE } } })

  const { data: reservationsResult, loading: loadingReservations } = useReservationsCreationQuery({
    variables: {
      filter: {
        tenantId: { eq: tenantId },
        propertyId: propertyId,
        id: { eq: reservationId },
      },
    },
    skip: !reservationId,
  })

  useEffect(() => {
    setLoading(loadingOrigins || loadingReservationStates || loadingBaseRateQuery || loadingReservations)
  }, [loadingOrigins, loadingReservationStates, loadingBaseRateQuery, loadingReservations])

  return { loading, originsResult, upsertReservationTime, reservationStatesResult, baseRateQuery, reservation: reservationsResult }
}
